import { CalendarClock, Loader2, Send, Sparkles, Trash2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { IconBtn } from "../../ui";
import { AttachButton, type ComposerAttachments } from "../_shared/attachments";
import { textToHtml } from "./richBody";

/**
 * The composer's send row: Send and Send later, the paperclip, the AI draft
 * button, and throwing the draft away. Its own file for the same reason as
 * `ComposerTitleBar`. The sending address moved to the From field at the top
 * (`SenderPick.tsx`), where it can be changed.
 */
export function ComposerFoot({
  submit,
  onSchedule,
  onDiscard,
  sending,
  tray,
  siteId,
  onAi,
  aiBusy,
  setBody,
}: {
  submit: () => void;
  /** Opens "Send later". */
  onSchedule: () => void;
  /** Throws the draft away - after asking, when there is one. */
  onDiscard: () => void;
  sending: boolean;
  tray: ComposerAttachments;
  siteId?: string | null;
  onAi?: () => Promise<string | null>;
  aiBusy?: boolean;
  setBody: (html: string) => void;
}) {
  const t = useT();
  return (
    <>
    {/* The send row is the bottom of the screen on a phone, so it has to
        clear the home indicator; on a docked window the inset is zero
        and the class costs nothing. */}
    <div className="flex flex-none items-center gap-2 border-t border-[var(--crm-hair)] px-4 pb-[calc(0.875rem+env(safe-area-inset-bottom,0px))] pt-3">
      {/* Send waits for the uploads. Letting it through would send the
          message with whatever happened to have finished, and the
          operator would have no way to tell which files made it. */}
      <button
        type="button"
        className="crm-btn crm-btn-dark crm-btn-sm"
        onClick={submit}
        disabled={sending || tray.busy}
        title={tray.busy ? t("compose.attachWait") : undefined}
      >
        {sending || tray.busy ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
        {t("compose.send")}
      </button>
      {/* Beside Send because it is a way of sending: the same letter, the same
          confirmation, only with a time on it. Waits for the uploads too. */}
      <IconBtn
        title={t("drafts.sendLater")}
        aria-label={t("drafts.sendLater")}
        disabled={sending || tray.busy}
        onClick={onSchedule}
      >
        <CalendarClock className="size-4" />
      </IconBtn>
      <AttachButton tray={tray} siteId={siteId} disabled={sending} />
      {onAi ? (
        <IconBtn
          title={t("compose.ai")}
          aria-label={t("compose.ai")}
          disabled={aiBusy}
          onClick={() => {
            // The model answers in plain text. Poured into the editor
            // rather than assigned, or its paragraph breaks would show
            // up as one run-on block the writer has to split by hand.
            void onAi().then((text) => {
              if (text) setBody(textToHtml(text));
            });
          }}
        >
          {aiBusy ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
        </IconBtn>
      ) : null}
      <IconBtn
        className="ml-auto"
        title={t("drafts.discard")}
        aria-label={t("drafts.discard")}
        disabled={sending}
        onClick={onDiscard}
      >
        <Trash2 className="size-4" />
      </IconBtn>
    </div>
    </>
  );
}
